import React, { useContext, useEffect, useRef } from "react";
import { useState } from "react";
import { systemCode, xs_languageCode } from "../..";
import MessageInfo from "../../common/MessageInfo";
import { getLabel } from "../../data/labels";
import { UserContext } from "../betslip/UserConf";
import { getConfiguration } from '../utils/configuration';
import { getArenaStreamingProps, getStreamingBalanceLimit } from "./streamingUtils";

function useScript(src,removeOnUnmount) {
    // Keep track of script status ("idle", "loading", "ready", "error")
    const [status, setStatus] = useState(src ? "loading" : "idle");
    useEffect(
      () => {
        // Allow falsy src value if waiting on other data needed for
        // constructing the script URL passed to this hook.
        if (!src) {
          setStatus("idle");
          return;
        }
        // Fetch existing script element by src
        // It may have been added by another intance of this hook
        let script = document.querySelector(`script[src="${src}"]`);
        if (!script) {
          // Create script
          script = document.createElement("script");
          script.src = src;
          script.async = true;
          script.setAttribute("data-status", "loading");
          // Add script to document body
          document.body.appendChild(script);
          // Store status in attribute on script
          // This can be read by other instances of this hook
          const setAttributeFromEvent = (event) => {
            script.setAttribute(
              "data-status",
              event.type === "load" ? "ready" : "error"
            );
          };
          script.addEventListener("load", setAttributeFromEvent);
          script.addEventListener("error", setAttributeFromEvent);
        } else {
          // Grab existing script status from attribute and set to state.
          setStatus(script.getAttribute("data-status"));
        }
        // Script event handler to update status in state
        // Note: Even if the script already exists we still need to add
        // event handlers to update the state for *this* hook instance.
        const setStateFromEvent = (event) => {
          setStatus(event.type === "load" ? "ready" : "error");
        };
        // Add event listeners
        script.addEventListener("load", setStateFromEvent);
        script.addEventListener("error", setStateFromEvent);
        // Remove event listeners on cleanup
        return () => {
          if (removeOnUnmount && script) {
            script.removeEventListener("load", setStateFromEvent);
            script.removeEventListener("error", setStateFromEvent);
          }
        };
      },
      [src] // Only re-run effect if script src changes
    );
    return status;
  }

function ArenaStreaming(props) {
    const conf = getConfiguration();
    const language = xs_languageCode;
    const getStreamingDataUrl = conf['api']['getLiveStreamingArenaData'];
    const userContext = useContext(UserContext);
    const user = userContext.getUser();
    const balanceLimit =  getStreamingBalanceLimit();
    const isBalanceLimited = balanceLimit >0 && balanceLimit > user.balance;
    //const iframeRef = useRef();
    const videoRef = useRef();
    const streamingId = props.streamId;

    const[streamingData,setStreamingData] = useState(null);
    //const[iframeUrl,setIframeUrl] = useState(null);
    const[streamSourceUrl,setStreamSourceUrl] = useState(null);
    const[thumbnailUrl,setThumbnailUrl] = useState(null);
    const [playerStarted,setPlayerStarted] = useState(false);
    const [isErr,setIsErr] = useState(false);
    const [errMsg,setErrMsg] = useState(null);

    const loadStreamingData = function () {
        try {
            const request = new XMLHttpRequest();
            const queryParams = [];
            queryParams.push("systemCode=" + systemCode);
            queryParams.push("hash=");
            queryParams.push("lingua=" + language);
            request.open('GET',getStreamingDataUrl + "?" + queryParams.join("&"), true);
            request.timeout = 60000;
            request.onreadystatechange = function() {
                try {
                    if (request.readyState == 4) {
                        if(request.status == 200) {
                            if(request.responseText){
                                const streamingData = JSON.parse(request.responseText);
                                try {
                                    if(streamingData?.status == 'OK') {
                                        setStreamingData(streamingData);
                                    }
                                }
                                catch(e){
                                    console.log(e);
                                    throw e;
                                }
                            }else{
                                throw new Error(getStreamingDataUrl+": Empty response");
                            }
                        }else{
                            throw new Error(getStreamingDataUrl+": Response code "+request.status);
                        }
                    }
                }catch(err) {
                    console.log(err);
                    setIsErr(true);
                }
            };
            request.send();
        }catch(err) {
            console.log(err);
            setIsErr(true);
        }
            
    }

    const loadFixturesData = function () {
        try {
            const arenaProps = getArenaStreamingProps();
            const fixturesUrl = arenaProps?.[0]?.afu?.replace('{{EVENTID}}',streamingId)
                                                    .replace('{{TOKEN}}',streamingData.token)
                                                    .replace('{{OPERATORID}}',streamingData.operatorId)
                                                    .replace('{{TIMESTAMP}}',streamingData.timestamp);
            const request = new XMLHttpRequest();
            request.open('GET',fixturesUrl, true);
            request.timeout = 60000;
            request.onreadystatechange = function() {
                try {
                    if (request.readyState == 4) {
                        if(request.status == 200) {
                            if(request.responseText){
                                const fixturesData = JSON.parse(request.responseText);
                                if(fixturesData.statusCode == 200){
                                    if(fixturesData.hlsUrl){
                                        /*const iframeUrl = arenaProps?.[0]?.aifu?.replace('{{EVENTID}}',streamingId)
                                                                                        .replace('{{TOKEN}}',streamingData.token)
                                                                                        .replace('{{OPERATORID}}',streamingData.operatorId)
                                                                                        .replace('{{TIMESTAMP}}',streamingData.timestamp);
                                        setIframeUrl(iframeUrl);*/
                                        setStreamSourceUrl(fixturesData.hlsUrl);
                                    }else if(fixturesData.thumbnailUrl) {
                                        setThumbnailUrl(fixturesData.thumbnailUrl);
                                    }else{
                                        setErrMsg(getLabel('live_page.stream_widget.stream_not_available'))
                                        throw new Error(fixturesUrl+": No thumbnail or stream URL found ");
                                    }
                                }else{
                                    throw new Error(fixturesUrl+": Status code "+fixturesData.statusCode);
                                }
                            }else{
                                throw new Error(fixturesUrl+": Empty response");
                            }
                        }else{
                            throw new Error(fixturesUrl+": Response code "+request.status);
                        }
                    }
                }catch(err){
                    console.log(err);
                    setIsErr(true);
                }
                
            }
            request.send();
        }catch(err){
            console.log(err);
            setIsErr(true);
        }
        
    }

    useEffect(() => {
        loadStreamingData();
        return () => {
            setIsErr(false);
            setErrMsg(null);
            //setIframeUrl(null);
            setThumbnailUrl(null);
        }

    }, [streamingId]);

    useEffect(() => {
        if(streamingData){
            loadFixturesData();
        }
    }, [streamingData]);


    function ArenaPlayer(props){
        const arenaProps = getArenaStreamingProps();
        const playerUrl = arenaProps?.[0]?.ajs;
        const status = useScript(playerUrl,true);

        useEffect(() => {
            if (typeof window.Hls !== 'undefined' && status == 'ready'  && streamSourceUrl && !playerStarted ) {
                const Hls = window.Hls;
                try {
                    var video = videoRef.current;
                    if(Hls?.isSupported()) {
                      var hls = new Hls();
                      hls.loadSource(streamSourceUrl);
                      hls.attachMedia(video);
                      hls.on(Hls.Events.MANIFEST_PARSED,function() {
                        video.play();
                        });
                    }
                    // hls.js is not supported on platforms that do not have Media Source Extensions (MSE) enabled.
                    // When the browser has built-in HLS support (check using `canPlayType`), we can provide an HLS manifest (i.e. .m3u8 URL) directly to the video element throught the `src` property.
                    // This is using the built-in support of the plain video element, without using hls.js.
                    else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                        video.src = streamSourceUrl;
                        video.addEventListener('canplay',function() {
                        video.play();
                        });
                    }
                    setPlayerStarted(true);
                }catch(err){
                    setIsErr(true);
                    console.log(err);
                }
            }
          }, [status])

          return (
            <>{isErr?
                errMsg || 'Error loading stream, something went wrong'
                :
                <>{streamSourceUrl && !playerStarted && 'Loading...'}</>
            }
            </>
        )
    }

    const videoConf = {
        'NETBET':{
            disablePictureInPicture:true,
            controlsList:"noplaybackrate nodownload nofullscreen"
        }
    }

    return (
		<>
		{user.logged || props.forceLogged?
			(!isBalanceLimited?
                <>
                <ArenaPlayer />
                {streamSourceUrl?
                    <video ref={videoRef} controls={true} {...videoConf[systemCode]} className="arena-live-stream-video" ></video>
                : thumbnailUrl &&
                    <img src={thumbnailUrl} alt="" className={"arena-live-stream-video"}></img>
                }
                </>
			:
			 <MessageInfo message={getLabel('live_page.stream_widget.insufficient_balance')} />
			)
		:
			<MessageInfo message={getLabel('live_page.stream_widget.must_login')} />
		}
        
		</>
    );

}

export default ArenaStreaming;